import React, { useState, useEffect } from 'react';
import { ExternalLink, Tv, ArrowRight, Stethoscope, Copy, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TvPanel() {
  const baseUrl = window.location.origin;
  const [copied, setCopied] = useState(null);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/tv/doctors`).then(res => setDoctors(res.data)).catch(() => {});
  }, []);

  const copyToClipboard = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const screens = [
    {
      id: 'pabellon-estetica',
      title: 'Pabellón Cirugía Estética',
      description: 'Muestra la cirugía en curso y las próximas del pabellón estético',
      url: `${baseUrl}/tv/pabellon?p=estetica`,
      color: '#D93025',
    },
    {
      id: 'pabellon-capilar',
      title: 'Pabellón Capilar',
      description: 'Muestra la cirugía en curso y las próximas del pabellón capilar',
      url: `${baseUrl}/tv/pabellon?p=capilar`,
      color: '#0B8043',
    },
    {
      id: 'recuperacion',
      title: 'Recuperación (Ambos pabellones)',
      description: 'Muestra todos los pacientes del día de ambas áreas con su estado actual',
      url: `${baseUrl}/tv/recuperacion`,
      color: '#4285F4',
    },
  ];

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="tv-panel-page">
      {/* TV Screens Section */}
      <div className="mb-10">
        <h1 className="text-2xl font-bold text-[#2B4C3B]">Pantallas TV</h1>
        <p className="text-[#6A736B] mt-1">
          URLs públicas para mostrar en televisores. No requieren inicio de sesión.
        </p>
      </div>

      <div className="space-y-4">
        {screens.map((screen) => (
          <div
            key={screen.id}
            data-testid={`tv-screen-${screen.id}`}
            className="bg-white rounded-xl border border-[#E8E5DD] p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${screen.color}15` }}
                >
                  <Tv size={22} style={{ color: screen.color }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#2B4C3B]">{screen.title}</h3>
                  <p className="text-sm text-[#6A736B] mt-1">{screen.description}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="text-xs bg-[#F3EBE1] text-[#2B4C3B] px-3 py-1.5 rounded-md font-mono break-all">
                      {screen.url}
                    </code>
                    <button
                      onClick={() => copyToClipboard(screen.url, screen.id)}
                      className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                      title="Copiar URL"
                      data-testid={`tv-copy-${screen.id}`}
                    >
                      {copied === screen.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} className="text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>
              <a
                href={screen.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`tv-open-${screen.id}`}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#2B4C3B] text-white rounded-lg hover:bg-[#1E3A2B] transition-colors text-sm font-medium flex-shrink-0"
              >
                <span>Abrir</span>
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Doctor Access Section */}
      <div className="mt-10 mb-6">
        <h2 className="text-xl font-bold text-[#2B4C3B]">Acceso Doctores</h2>
        <p className="text-[#6A736B] mt-1">
          Links públicos para que cada doctor vea su agenda sin iniciar sesión. Compatible con celular y PC.
        </p>
      </div>

      {doctors.length > 0 ? (
        <div className="space-y-4">
          {doctors.map((doc) => {
            const url = `${baseUrl}/agenda/${doc.id}`;
            return (
              <div
                key={doc.id}
                data-testid={`doctor-link-${doc.id}`}
                className="bg-white rounded-xl border-2 border-[#7C3AED]/15 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#7C3AED]/10">
                      <Stethoscope size={22} className="text-[#7C3AED]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#2B4C3B]">{doc.name}</h3>
                      <p className="text-sm text-[#6A736B] mt-1">Agenda personal — sin login, con filtro mis/todas cirugías</p>
                      <div className="mt-3 flex items-center gap-2">
                        <code className="text-xs bg-[#F3EBE1] text-[#2B4C3B] px-3 py-1.5 rounded-md font-mono break-all">
                          {url}
                        </code>
                        <button
                          onClick={() => copyToClipboard(url, doc.id)}
                          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                          title="Copiar URL"
                          data-testid={`doctor-copy-${doc.id}`}
                        >
                          {copied === doc.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} className="text-gray-400" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`doctor-open-${doc.id}`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#7C3AED] text-white rounded-lg hover:bg-[#6D28D9] transition-colors text-sm font-medium flex-shrink-0"
                  >
                    <span>Abrir</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8E5DD] p-8 text-center">
          <Stethoscope size={32} className="mx-auto text-[#6A736B]/30 mb-2" />
          <p className="text-sm text-[#6A736B]">No hay doctores registrados. Crea usuarios con rol "Doctor" en la sección Usuarios.</p>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 bg-[#F3EBE1]/50 rounded-xl border border-[#E8E5DD] p-6">
        <h3 className="text-sm font-semibold text-[#2B4C3B] mb-2">Instrucciones TV</h3>
        <ul className="space-y-2 text-sm text-[#6A736B]">
          <li className="flex items-start gap-2">
            <ArrowRight size={14} className="mt-1 flex-shrink-0 text-[#2B4C3B]" />
            Abre la URL correspondiente en el navegador del televisor
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight size={14} className="mt-1 flex-shrink-0 text-[#2B4C3B]" />
            Las pantallas se actualizan automáticamente cada 30 segundos
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight size={14} className="mt-1 flex-shrink-0 text-[#2B4C3B]" />
            No requieren inicio de sesión ni credenciales
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight size={14} className="mt-1 flex-shrink-0 text-[#2B4C3B]" />
            Usa el modo pantalla completa del navegador (F11) para mejor visualización
          </li>
        </ul>
      </div>
    </div>
  );
}
